
import { Button } from '../../../../components/ui/button';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { LooseRelationship } from '../../../../types/electron';
import { ResultsGrid } from '../../common/components/ResultsGrid';
import { formatRowCount } from '../../common/utils/formatRowCount';
import { useInboundForeignKeyRows } from '../hooks/useInboundForeignKeyRows';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
export function InboundForeignKeyRowsPreview(props: {
  readonly connectionId: string;
  readonly database: string;
  readonly foreignKey: TableExplorerInboundForeignKey;
  readonly parentRow: Record<string, unknown>;
  readonly looseRel?: LooseRelationship;
}) {
  const { data, isLoading, error, refetch, missingColumns } = useInboundForeignKeyRows({
    connectionId: props.connectionId,
    database: props.database,
    foreignKey: props.foreignKey,
    parentRow: props.parentRow,
    enabled: true,
    limit: 100,
    looseRel: props.looseRel,
  });

  if (missingColumns.length > 0) {
    return (
      <div className="px-2 pb-2 text-xs text-muted-foreground">
        Missing referenced column value(s): {missingColumns.join(', ')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 pb-2 text-xs text-muted-foreground">
        <RandomDotmSpinner size={14} />
        Loading related rows…
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-2 pb-2">
        <div className="text-xs text-destructive">Failed to load related rows</div>
        <div className="text-xs text-muted-foreground mt-1">
          {error instanceof Error ? error.message : String(error)}
        </div>
        <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs mt-2" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const rows = data?.rows || [];
  const columns = data?.columns || [];

  if (rows.length === 0) {
    return <div className="px-2 pb-2 text-xs text-muted-foreground">No related rows found.</div>;
  }

  // This line is the single row-count readout for the preview — the embedded
  // grid's own toolbar count is suppressed (`showRowCountSummary={false}`) so
  // the same fact isn't stated twice, one line apart. Because the preview is
  // capped at `limit`, it also absorbs the total the grid toolbar used to carry.
  const totalRowCount = data?.totalRowCount ?? rows.length;
  const summary =
    totalRowCount > rows.length
      ? `Showing ${rows.length.toLocaleString()} of ${formatRowCount(totalRowCount)}`
      : `Showing ${formatRowCount(rows.length)}`;

  return (
    <div className="px-2 pb-2">
      <div className="text-xs text-muted-foreground mb-1 tabular-nums" data-testid="inbound-fk-rows-summary">
        {summary}
      </div>
      <div className="h-56 overflow-hidden rounded-md border border-border bg-background">
        <ResultsGrid
          resultSets={[
            {
              columns,
              rows,
              rowCount: totalRowCount,
            },
          ]}
          analyticsSurface="table_explorer"
          // FIX-2 (CP-2) — was already accepted as props but never forwarded,
          // leaving Copy INSERT/Export SQL in this preview grid unable to
          // resolve identity columns (silent, non-degrading gap: no
          // connection/database meant `fetchIdentityColumnsForResultSet`
          // always skipped the fetch and emitted the legacy, unwrapped
          // script). Threading them lets identity awareness work here too.
          connectionId={props.connectionId}
          database={props.database}
          showRowCountSummary={false}
        />
      </div>
    </div>
  );
}

